import { I18n } from '@calendly/i18n';
import { BareButton } from '@calendly/ui/components/bare-button';
import { useDropdown } from '@calendly/ui/components/dropdown';
import { ArrowChevronDownMiniIcon, ArrowChevronUpMiniIcon } from '@calendly/ui/components/icons';
import { Loader } from '@calendly/ui/components/loader';
import { CheckboxField } from '@calendly/ui/form/checkbox-field';
import { useDeviceType } from '@calendly/ui/hooks/use-device-type';
import { primaryColorL1 } from '@calendly/ui/theme';
import classNames from 'classnames';
import { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import SearchInput from 'components/core/search_input';
import { MenuActionItem } from 'components/filter_dropdown_menu/styles';
import { useViewportFit } from 'hooks/useViewportFit';
import { useGetPackagesQuery, useLazyGetPackagesQuery } from 'shared/api/commerce/graphql_api';
import { CloseButton, Container, DropdownButton, FilterHeader, ItemActionsContainer, ItemsActionButton } from './dropdown/styles';
import { FilterLayout } from './filter_layout';
const PAGE_SIZE = 20;
const menuBodyCss = "m12vet1y";
const listCss = "l15gsjv2";
const showMoreButtonCss = "s1p2zpab";
const ShowMoreButton = forwardRef((props, ref) => <BareButton ref={ref} {...props} className={classNames(showMoreButtonCss, props.className)} />);
export const PackagesSelect = ({
  params,
  onApply,
  useColumnLayout = false
}) => {
  const [toggle, Dropdown, isExpanded] = useDropdown();
  const {
    ref,
    rightConflict
  } = useViewportFit();
  const isPhone = useDeviceType('isPhone');
  const [searchTerm, setSearchTerm] = useState('');
  const [extraPages, setExtraPages] = useState([]);
  const prevSearchRef = useRef(searchTerm);
  const queryArgs = useMemo(() => ({
    packageType: 'MEETING_PACKAGE',
    status: 'ACTIVE',
    search: searchTerm || undefined,
    count: PAGE_SIZE
  }), [searchTerm]);
  const {
    data,
    isLoading,
    isFetching
  } = useGetPackagesQuery(queryArgs);
  const [fetchMore, {
    isFetching: isFetchingMore
  }] = useLazyGetPackagesQuery();
  useEffect(() => {
    if (prevSearchRef.current !== searchTerm) {
      setExtraPages([]);
      prevSearchRef.current = searchTerm;
    }
  }, [searchTerm]);
  const firstPagePackages = useMemo(() => {
    if (!data?.packages?.collection) return [];
    return data.packages.collection;
  }, [data]);
  const nextPageToken = data?.packages?.pagination?.nextPageToken;
  const hasMore = Boolean(nextPageToken) && extraPages.length === 0 || extraPages.length > 0 && Boolean(extraPages[extraPages.length - 1]?.pagination?.nextPageToken);
  const allPackages = useMemo(() => {
    const extra = extraPages.flatMap(page => page.collection);
    return [...firstPagePackages, ...extra];
  }, [firstPagePackages, extraPages]);
  const selectedUuids = params.package_uuids || [];
  const controlText = selectedUuids.length === 0 ? I18n.t('publisher.dashboard.filters.label.package_all', {
    defaultValue: 'All Meeting Packages'
  }) : I18n.t('publisher.dashboard.filters.label.package', {
    count: selectedUuids.length,
    defaultValue: `${selectedUuids.length} package(s)`
  });
  const onShowMore = useCallback(async () => {
    const token = extraPages.length > 0 ? extraPages[extraPages.length - 1]?.pagination?.nextPageToken : nextPageToken;
    if (!token) return;
    const result = await fetchMore({
      packageType: 'MEETING_PACKAGE',
      status: 'ACTIVE',
      search: searchTerm || undefined,
      count: PAGE_SIZE,
      pageToken: token
    });
    if (result.data?.packages) {
      setExtraPages(prev => [...prev, result.data.packages]);
    }
  }, [extraPages, nextPageToken, fetchMore, searchTerm]);
  const onSelect = uuid => {
    const newUuids = selectedUuids.includes(uuid) ? selectedUuids.filter(id => id !== uuid) : [...selectedUuids, uuid];
    onApply({
      ...params,
      package_uuids: newUuids
    });
  };
  const onSelectAll = () => {
    onApply({
      ...params,
      package_uuids: allPackages.map(pkg => pkg.uuid)
    });
  };
  const onClearAll = () => {
    onApply({
      ...params,
      package_uuids: []
    });
  };
  return <FilterLayout label={I18n.t('publisher.labels.package', {
    defaultValue: 'Meeting Package'
  })} useColumnLayout={useColumnLayout}>
      <div>
        <DropdownButton onClick={toggle}>
          {controlText}
          {isExpanded ? <ArrowChevronUpMiniIcon color={primaryColorL1} /> : <ArrowChevronDownMiniIcon color={primaryColorL1} />}
        </DropdownButton>
        <Dropdown>
          <Container data-component="filter-dropdown-menu" ref={ref} hasRightConflict={rightConflict}>
            <FilterHeader>
              <SearchInput className="mbm" placeholder={I18n.t('publisher.meetings.filter_modal.dropdown.search_placeholder')} autofocus onChange={setSearchTerm} />
              <ItemActionsContainer>
                <ItemsActionButton onClick={onSelectAll}>
                  {I18n.t('publisher.dashboard.filters.select_all')}
                </ItemsActionButton>
                <span> / </span>
                <ItemsActionButton onClick={onClearAll}>
                  {I18n.t('publisher.dashboard.filters.clear')}
                </ItemsActionButton>
              </ItemActionsContainer>
            </FilterHeader>
            {isLoading ? <Loader className="mvl" /> : <div className={menuBodyCss}>
                <ul className={listCss}>
                  {allPackages.map(pkg => <MenuActionItem key={pkg.uuid}>
                      <CheckboxField label={pkg.name} checked={selectedUuids.includes(pkg.uuid)} onChange={() => onSelect(pkg.uuid)} />
                    </MenuActionItem>)}
                  {hasMore && <MenuActionItem>
                      {isFetchingMore ? <Loader /> : <ShowMoreButton onClick={onShowMore}>
                          {I18n.t('publisher.title.show_more')}
                        </ShowMoreButton>}
                    </MenuActionItem>}
                </ul>
              </div>}
            {isFetching && !isLoading && <Loader className="mvl" />}
            {isPhone && <CloseButton closeDropdown={toggle}>
                {I18n.t('publisher.action.close')}
              </CloseButton>}
          </Container>
        </Dropdown>
      </div>
    </FilterLayout>;
};

require("./packages_select.wyw-in-js.css!=!../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./packages_select.js");